/**
 * IMPORTANT: 
 *   This module is used both on the server side and on the client side!
 * 
 *   It is FORBIDDEN to include configuration modules or variables here
 *   containing secret data that MUST NOT get to the CLIENT SIDE!
 */
'use strict';
const moment = require('moment');
const isEmpty = require('lodash/isEmpty');
const isEqual = require('lodash/isEqual');
const isNull = require('lodash/isNull');
const isString = require('lodash/isString');
const omitBy = require('lodash/omitBy');
const getPath = require('lodash/get');
const isExactNaN = require('lodash/isNaN');
const merge = require('lodash/merge');
const draftToHtml = require('draftjs-to-html');
const {
  getPlanFeatures,
  getPlanFeatureLimits,
  getPlanFees,
  getPlanLimits,
  getPlanStatus,
  getPublisherPlan,
  getPublisherPlanInfo,
  isPublisherPlan,
  isPublisherReferredByPartner,
  isPublisherClass2,
  getSoundwiseProductInfo,
  getPlanFeatureAvailability,
  canPublisherAccessFeature,
} = require('./planTiers');
const {
  getPartners,
  getPartnerCodesValidity,
  getAllPartnerCodes,
  getPartnerCodes,
} = require('./partners');
const { whiteLabelAppConfig } = require('../../config-common'); 

const podcastCategories = {
  '1301': {
    id: '1301',
    name: 'Arts',
    subCategories: {
      '1482': { id: '1482', name: 'Books' },
      '1402': { id: '1402', name: 'Design' },
      '1459': { id: '1459', name: 'Fashion & Beauty' },
      '1306': { id: '1306', name: 'Food' },
      '1405': { id: '1405', name: 'Performing Arts' },
      '1406': { id: '1406', name: 'Visual Arts' },
    },
  },
  '1321': {
    id: '1321',
    name: 'Business',
    subCategories: {
      '1410': { id: '1410', name: 'Careers' },
      '1493': { id: '1493', name: 'Entrepreneurship' },
      '1412': { id: '1412', name: 'Investing' },
      '1491': { id: '1491', name: 'Management' },
      '1492': { id: '1492', name: 'Marketing' },
      '1494': { id: '1494', name: 'Non-Profit' },
    },
  },
  '1303': {
    id: '1303',
    name: 'Comedy',
    subCategories: {
      '1496': { id: '1496', name: 'Comedy Interviews' },
      '1495': { id: '1495', name: 'Improv' },
      '1497': { id: '1497', name: 'Stand-Up' },
    },
  },
  '1304': {
    id: '1304',
    name: 'Education',
    subCategories: {
      '1501': { id: '1501', name: 'Courses' },
      '1499': { id: '1499', name: 'How To' },
      '1498': { id: '1498', name: 'Language Learning' },
      '1500': { id: '1500', name: 'Self-Improvement' },
    },
  },
  '1483': {
    id: '1483',
    name: 'Fiction',
    subCategories: {
      '1486': { id: '1486', name: 'Comedy Fiction' },
      '1484': { id: '1484', name: 'Drama' },
      '1485': { id: '1485', name: 'Science Fiction' },
    },
  },
  '1511': { id: '1511', name: 'Government', subCategories: {} },
  '1512': {
    id: '1512',
    name: 'Health & Fitness',
    subCategories: {
      '1513': { id: '1513', name: 'Alternative Health' },
      '1514': { id: '1514', name: 'Fitness' },
      '1518': { id: '1518', name: 'Medicine' },
      '1517': { id: '1517', name: 'Mental Health' },
      '1515': { id: '1515', name: 'Nutrition' },
      '1516': { id: '1516', name: 'Sexuality' },
    },
  },
  '1487': { id: '1487', name: 'History', subCategories: {} },
  '1305': {
    id: '1305',
    name: 'Kids & Family',
    subCategories: {
      '1519': { id: '1519', name: 'Education for Kids' },
      '1521': { id: '1521', name: 'Parenting' },
      '1522': { id: '1522', name: 'Pets & Animals' },
      '1520': { id: '1520', name: 'Stories for Kids' },
    },
  },
  '1502': {
    id: '1502',
    name: 'Leisure',
    subCategories: {
      '1510': { id: '1510', name: 'Animation & Manga' },
      '1503': { id: '1503', name: 'Automotive' },
      '1504': { id: '1504', name: 'Aviation' },
      '1506': { id: '1506', name: 'Crafts' },
      '1507': { id: '1507', name: 'Games' },
      '1505': { id: '1505', name: 'Hobbies' },
      '1508': { id: '1508', name: 'Home & Garden' },
      '1509': { id: '1509', name: 'Video Games' },
    },
  },
  '1310': {
    id: '1310',
    name: 'Music',
    subCategories: {
      '1523': { id: '1523', name: 'Music Commentary' },
      '1524': { id: '1524', name: 'Music History' },
      '1525': { id: '1525', name: 'Music Interviews' },
    },
  },
  '1489': {
    id: '1489',
    name: 'News',
    subCategories: {
      '1490': { id: '1490', name: 'Business News' },
      '1526': { id: '1526', name: 'Daily News' },
      '1531': { id: '1531', name: 'Entertainment News' },
      '1530': { id: '1530', name: 'News Commentary' },
      '1527': { id: '1527', name: 'Politics' },
      '1529': { id: '1529', name: 'Sports News' },
      '1528': { id: '1528', name: 'Tech News' },
    },
  },
  '1314': {
    id: '1314',
    name: 'Religion & Spirituality',
    subCategories: {
      '1438': { id: '1438', name: 'Buddhism' },
      '1439': { id: '1439', name: 'Christianity' },
      '1463': { id: '1463', name: 'Hinduism' },
      '1440': { id: '1440', name: 'Islam' },
      '1441': { id: '1441', name: 'Judaism' },
      '1532': { id: '1532', name: 'Religion' },
      '1444': { id: '1444', name: 'Spirituality' },
    },
  },
  '1533': {
    id: '1533',
    name: 'Science',
    subCategories: {
      '1538': { id: '1538', name: 'Astronomy' },
      '1539': { id: '1539', name: 'Chemistry' },
      '1540': { id: '1540', name: 'Earth Sciences' },
      '1541': { id: '1541', name: 'Life Sciences' },
      '1536': { id: '1536', name: 'Mathematics' },
      '1534': { id: '1534', name: 'Natural Sciences' },
      '1537': { id: '1537', name: 'Nature' },
      '1542': { id: '1542', name: 'Physics' },
      '1535': { id: '1535', name: 'Social Sciences' },
    },
  },
  '1324': {
    id: '1324',
    name: 'Society & Culture',
    subCategories: {
      '1543': { id: '1543', name: 'Documentary' },
      '1302': { id: '1302', name: 'Personal Journals' },
      '1443': { id: '1443', name: 'Philosophy' },
      '1320': { id: '1320', name: 'Places & Travel' },
      '1544': { id: '1544', name: 'Relationships' },
    },
  },
  '1545': {
    id: '1545',
    name: 'Sports',
    subCategories: {
      '1549': { id: '1549', name: 'Baseball' },
      '1548': { id: '1548', name: 'Basketball' },
      '1554': { id: '1554', name: 'Cricket' },
      '1560': { id: '1560', name: 'Fantasy Sports' },
      '1547': { id: '1547', name: 'Football' },
      '1553': { id: '1553', name: 'Golf' },
      '1550': { id: '1550', name: 'Hockey' },
      '1552': { id: '1552', name: 'Rugby' },
      '1551': { id: '1551', name: 'Running' },
      '1546': { id: '1546', name: 'Soccer' },
      '1558': { id: '1558', name: 'Swimming' },
      '1556': { id: '1556', name: 'Tennis' },
      '1557': { id: '1557', name: 'Volleyball' },
      '1559': { id: '1559', name: 'Wilderness' },
      '1555': { id: '1555', name: 'Wrestling' },
    },
  },
  '1309': {
    id: '1309',
    name: 'TV & Film',
    subCategories: {
      '1562': { id: '1562', name: 'After Shows' },
      '1564': { id: '1564', name: 'Film History' },
      '1565': { id: '1565', name: 'Film Interviews' },
      '1563': { id: '1563', name: 'Film Reviews' },
      '1561': { id: '1561', name: 'TV Reviews' },
    },
  },
  '1318': { id: '1318', name: 'Technology', subCategories: {} },
  '1488': { id: '1488', name: 'True Crime', subCategories: {} },
};

const currencies = [
  ['USD', 'United States Dollar'],
  ['EUR', 'Euro'],
  ['JPY', 'Japanese Yen'],
  ['GBP', 'British Pound'],
  ['AUD', 'Australian Dollar'],
  ['CAD', 'Canadian Dollar'],
  ['CHF', 'Swiss Franc'],
  ['CNY', 'Chinese Renminbi Yuan'],
  ['CZK', 'Czech Koruna'],
  ['SEK', 'Swedish Krona'],
  ['MXN', 'Mexican Peso'],
  ['NZD', 'New Zealand Dollar'],
  ['SGD', 'Singapore Dollar'],
  ['HKD', 'Hong Kong Dollar'],
  ['HUF', 'Hungarian Forint'],
  ['NOK', 'Norwegian Krone'],
  ['KRW', 'South Korean Won'],
  ['TRY', 'Turkish New Lira'],
  ['INR', 'Indian Rupee'],
  ['RUB', 'Russian Ruble'],
  ['BRL', 'Brazilian Real'],
  ['ZAR', 'South African Rand'],
  ['DKK', 'Danish Krone'],
  ['PLN', 'Polish Złoty'],
  ['RON', 'Romanian Leu'],
  ['TWD', 'New Taiwan Dollar'],
  ['THB', 'Thai Baht'],
  ['MYR', 'Malaysian Ringgit'],
  ['NGN', 'Nigerian Naira'],
  ['AED', 'United Arab Emirates Dirham'],
  ['BGN', 'Bulgarian Lev'],
];

// these currencies need to be sent in regular denominations
// https://stripe.com/docs/currencies#zero-decimal
const getStripeAmount = (amount, currency, inverse = false) => {
  amount = Number(amount);
  currency = (currency || 'USD').toUpperCase();
  const zeroDecimals = [
    'BIF',
    'DJF',
    'JPY',
    'KRW',
    'PYG',
    'VUV',
    'XOF',
    'CLP',
    'UGX',
    'GNF',
    'KMF',
    'MGA',
    'RWF',
    'XAF',
    'XPF',
    'VND',
  ];

  // if not a zero decimal value, multiple the value with 100
  if (!zeroDecimals.includes(currency)) {
    return inverse ? amount / 100 : amount * 100;
  }

  return amount;
};

const getStripeCurrency = (currency = false) => {
  return (currency || 'usd').toLowerCase();
};

// minimum charge amounts per currency
// https://stripe.com/docs/currencies#minimum-and-maximum-charge-amounts
const getStripeMinimumCharge = curreny => {
  const minimumCharges = {
    USD: 0.5,
    EUR: 0.5,
    JPY: 50,
    GBP: 0.3,
    AUD: 0.5,
    CAD: 0.5,
    CHF: 0.5,
    CZK: 15.0,
    SEK: 3.0,
    MXN: 10,
    NZD: 0.5,
    SGD: 0.5,
    HKD: 4.0,
    NOK: 3.0,
    INR: 0.5,
    BRL: 0.5,
    DKK: 2.5,
    PLN: 2.0,
    RON: 2.0,
    MYR: 2.0,
    AED: 2.0,
    BGN: 1.0,
    HUF: 175.0,

    // according to the exchange rate of converting from USD 0.5
    // https://freecurrencyrates.com/en/USD-exchange-rate-calculator
    CNY: 6.5,
    KRW: 567.5,
    TRY: 3.78,
    RUB: 36.66,
    ZAR: 7.47,
    TWD: 14.06,
    THB: 15.36,
    NGN: 188.88,
  };

  return curreny in minimumCharges ? minimumCharges[curreny] : 0;
};

// deprecated, replace with encodeStripePlanID
const generateStripePlanId = data => {
  const { publisherId, soundcastId, title, billingCycle, price } = data;
  return `${publisherId}-${soundcastId}-${title}-${billingCycle}-${price}`;
};

// specifically for API authentications
const getAuthSupportedPlan = () => ['pro', 'platinum'];

const isCouponBelongsToPlan = (coupon = '', selectedPlan = {}) => {
  if (isEmpty(coupon) || isEmpty(selectedPlan)) {
    return false;
  }

  const { coupons = [] } = selectedPlan;
  const validCoupons = coupons.map(coupon => coupon.code);
  return validCoupons.includes(coupon);
};

const isProduction = () => process.env.NODE_ENV === 'production';
const isStaging = () => process.env.NODE_ENV === 'staging';
const isLocalDev = () => process.env.NODE_ENV === 'dev';

const isFreePlan = ({ price = 0 }) => {
  const isFree = isString(price) && price.toLowerCase() === 'free';
  return isFree || Number(price) === 0;
};

// deprecated, use makePlanFromCoupon in future
const getUpdatedPlanFromCoupon = (promoCode, selectedPlan) => {
  return new Promise((resolve, reject) => {
    const isBelong = isCouponBelongsToPlan(promoCode, selectedPlan);
    const { price, coupons = [] } = selectedPlan;
    const validPromoCodes = coupons.map(coupon => coupon.code);
    if (isBelong && validPromoCodes.includes(promoCode)) {
      const index = validPromoCodes.indexOf(promoCode);
      const coupon = coupons[index];
      if (moment().format('X') < coupon.expiration) {
        const isTrial =
          coupon.couponType === 'trial_period' ? coupon.trialLength.toString() : false;
        const total = !isTrial ? Number((price * (100 - coupon.percentOff)) / 100).toFixed(2) : 0;

        resolve({
          plan: Object.assign({}, selectedPlan, { price: total }),
          isTrial, // false or Number.toString()
          discounted: Number(total) < price,
          info: isTrial
            ? `* ${coupon.trialLength} day trial period`
            : `* ${coupon.percentOff}% discount`,
        });
      } else {
        reject(new Error(`This promo code has expired`));
      }
    } else {
      reject(new Error(`Promo code doesn't exist`));
    }
  });
};

// local
const getCouponInfoSafe = coupon => {
  const { code, expiration = 0, couponType, percentOff = null, trialLength = null } = coupon || {};
  let couponInfo = null;
  try {
    couponInfo = `${code}: ${couponType} ` +
      `${couponType === 'trial_period' ? trialLength + 'd' : percentOff + '%'} ` +
      `exp-${moment.unix(expiration).format('YYYY-MM-DD')}`;
  } catch (err) {
    couponInfo = `${code}: ${JSON.stringify(coupon)}`;
  }
  return couponInfo;
};

const getCouponInfo = (couponCode, checkedPlan) => {
  const { coupons } = checkedPlan || {};
  const coupon = (coupons || []).find(coupon => isEqual(coupon.code, couponCode)) || [];
  return isEmpty(coupon)
    ? `${couponCode}: This promo code doesn't exist`
    : getCouponInfoSafe(coupon);
};

const makePlanFromCoupon = (couponCode, checkedPlan, isTrialFirstPay0 = true) => {
  return new Promise((resolve, reject) => {
    const {
      price, // Number.toString() or 'free'
      coupons = [],
    } = checkedPlan || {};
    const coupon = coupons.find(coupon => isEqual(coupon.code, couponCode)) || {};
    if (isEmpty(coupon)) {
      return reject(new Error(`Promo code doesn't exist`));
    }
    const { expiration = 0, couponType, percentOff = null, trialLength = null } = coupon;
    if (moment().format('X') > expiration) {
      return reject(new Error(`This promo code has expired`));
    }
    const couponInfo = getCouponInfoSafe(coupon);

    const isTrialPeriod = couponType === 'trial_period';
    const trialDays = isTrialPeriod && !isNull(trialLength) ? Number(trialLength) : 0;
    const isTrial = isTrialPeriod && trialDays > 0;

    const nPercentOff = isNull(percentOff) ? 0 : Number(percentOff);
    const updateedPrice = isTrial ? (isTrialFirstPay0 ? '0' : price)
      : isFreePlan({ price }) ? '0'
      : Number((Number(price) * (100 - nPercentOff)) / 100).toFixed(2);
    const plan = {
      ...checkedPlan,
      price: updateedPrice,
    };
    const isFree = isFreePlan(plan);
    const isDiscounted = Number(updateedPrice) < Number(price);

    // console.log(`***** makePlanFromCoupon ${JSON.stringify({ price, updateedPrice, isFree, isTrial })}`); // DBG

    resolve({
      plan,
      trialDays, // Number
      isTrial, // Boolean
      isFree, // no payment required
      isDiscounted,
      couponInfo,
      info: isTrial
        ? `* ${coupon.trialLength} day(s) trial period`
        : `* ${coupon.percentOff}% discount`,
    });
  });
};

const isSoundcastDripped = ({ type = 'general' }) => {
  return type === 'drip';
};

const accessExpirationWithGracePeriod = (accessEndUnix = 0, gracePeriod = 1) => {
  return moment.unix(accessEndUnix).add(gracePeriod, 'days').unix();
};

const removeAccessExpirationGracePeriod = (accessEndUnix = 0, gracePeriod = 1) => {
  return moment.unix(accessEndUnix).subtract(gracePeriod, 'days').unix();
};

const convertDecimalCommasToDots = value => {
  const converted = `${value}`.replace(/\s/g, '').replace(',', '.');

  // it should be a string representing number
  if (!isNaN(converted)) {
    return converted;
  }

  // if it goes here its probably not a number
  const fvalue = parseFloat(converted);

  // check the parsed value, return empty if its NaN
  if (isExactNaN(fvalue)) {
    return '';
  }

  return fvalue;
};

const createContentPush = (type, announcementId, soundcastId, title, body) => {
  return {
    data: { type, announcementId, soundcastId },
    notification: { title, body },
  };
};

const getRandomColor = () => {
  var letters = '0123456789ABCDEF';
  var color = '';
  for (var i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

const isFreeAccount = userInfo => {
  const currentTime = moment().format('X');
  if (
    userInfo &&
    userInfo.publisher &&
    userInfo.publisher.plan &&
    userInfo.publisher.current_period_end > currentTime
  ) {
    return false;
  }
  return true;
};

const isWithinDaysRange = (past, range) => !(moment().diff(moment(past), 'days') > range);
const isLaterDateBefore = (date, before) => moment(date).isBefore(moment(before));
const isValidScheduledDate = unixDate => {
  const date = moment.unix(unixDate);
  return !isLaterDateBefore(date, moment().startOf('hour'));
};

const slugify = text => {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w\-]+/g, '') // Remove all non-word chars
    .replace(/\-\-+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, ''); // Trim - from end of text
};

const lastnamePrivacy = (str = '') => {
  if (isEmpty(str)) return '';
  return `${str.charAt(0).toUpperCase()}.`;
};

const replaceSpecialTags = (template, locals) => {
  const { user = {}, soundcast = {}, publisher = {}, soundcastId = '' } = locals;
  const { firstName = '' } = user;
  const { title: soundcastTitle = '' } = soundcast;
  const { name: publisherTitle = '' } = publisher;
  const {
    appName = '', 
    appstoreUrl = '',
    playstoreUrl = '', 
  } = extractPublisherIAPHubDetails(publisher);
  return [
    ['[subscriber first name]', firstName],
    ['[subscriber_first_name]', firstName],
    ['[subscriber%20first%20name]', firstName],
    ['[soundcast title]', soundcastTitle],
    ['[soundcast_title]', soundcastTitle],
    ['[soundcast%20title]', soundcastTitle],
    ['[publisher name]', publisherTitle],
    ['[publisher_name]', publisherTitle],
    ['[publisher%20name]', publisherTitle],
    ['[soundcast id]', soundcastId],
    ['[soundcast_id]', soundcastId],
    ['[soundcast%20id]', soundcastId],
    // White label app links
    ['[app name]', appName],
    ['[app_name]', appName],
    ['[app%20name]', appName],
    ['https://wl.app.appstore.url/', appstoreUrl],
    ['https://wl.app.appstore.url', appstoreUrl],
    ['https://wl.app.playstore.url/', playstoreUrl],
    ['https://wl.app.playstore.url', playstoreUrl],
  ].reduce((accum, [keyword, value]) => {
    return !isEmpty(value) ? accum.replace(keyword, value) : accum;
  }, template);
};

const trimSpaces = str => (str || '').replace(/\s+/g, '');

const httpToHttpsUrl = url => (url || '').replace(/^http:\/\//i, 'https://');

const restoreDots = x => x.replace(/\(dot\)/g, '.');
const replaceDots = x => x.replace(/\./g, '(dot)');
const replaceDotsSafe = x => replaceDots(x || '').trim().toLowerCase();

const randomStringOfSize = size =>
  Math.random()
    .toString(36)
    .slice(-1 * size);

const cloudfrontToS3Url = url =>
  (url || '').replace(`d1jzcuf08rvzm.cloudfront.net`, `s3.amazonaws.com/soundwiseinc`);

const polishImageUrl = url => httpToHttpsUrl(cloudfrontToS3Url(url));

const parseDraftJsTemplate = jsonTemplate => {
  const editorState = JSON.parse(jsonTemplate);
  return draftToHtml(editorState);
};

// Determine if soundcast is a bundle and attach soundcast.soundcastsIncluded ids
const getSoundcastsIds = (soundcastId, soundcast) => {
  const isBundle = !!(soundcast && soundcast.bundle); // boolean
  const bundleId = isBundle ? soundcastId : null;
  const soundcastIDs = [soundcastId];
  if (isBundle && soundcast.soundcastsIncluded && soundcast.soundcastsIncluded.length) {
    for (const el of soundcast.soundcastsIncluded) {
      soundcastIDs.push(el);
    }
  }
  return { isBundle, bundleId, soundcastIDs };
};

const getEmailBody = (options, invitees) => {
  const { publisherName, episodeTitle, landingPage, soundcastId, soundcastTitle } = options;
  if (!invitees) {
    return `<p>Hi <span>[%first_name | there%]</span>!</p><p></p><p>${publisherName} just published <strong>${episodeTitle}</strong> in <a href="${
      landingPage ? 'https://mysoundwise.com/soundcasts/' + soundcastId : ''
    }" target="_blank">${soundcastTitle}</a>. </p><p></p><p>Go check it out on the Soundwise app!</p>`;
  } else {
    return `<p>Hi there!</p><p></p><p>${publisherName} just published <strong>${episodeTitle}</strong> in <a href="${
      landingPage ? 'https://mysoundwise.com/soundcasts/' + soundcastId : ''
    }" target="_blank">${soundcastTitle}</a>. </p><p></p><p>To listen to the track, simply accept your invitation to subscribe to <i>${soundcastTitle}</i> on the Soundwise app!</p>`;
  }
};

const getCurrentUnixTimestamp = () => moment().unix();

const getAudioWorkerRootHost = (key = '') => {
  const urls = {
    production: 'https://app.mysoundwise.com:8080',
    dev: 'https://preprod.mysoundwise.com:8080',
    local: 'http://localhost:4000',
  };

  const urlkey = isEmpty(key) ? process.env.NODE_ENV : key;
  return urls[urlkey];
};

// email validator https://stackoverflow.com/a/46181/1460756
const emailValidator = value => {
  // 2022-06-08 @knowledgemd.com - ban this domain from creating new accounts
  if (String(value).toLowerCase().includes('@knowledgemd.com')) {
    return false;
  }
  const email = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return email.test(String(value).toLowerCase());
};

// https://stripe.com/docs/statement-descriptors#requirements
const removeStripeInvalidCharacters = str => {
  return str.replace(/[\<\>\\\'\"\*]/gi, '');
};

const safeMathRound = num => Math.round((Number(num) + Number.EPSILON) * 100) / 100;

const isJSONString = str => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }

  return true;
};

const attachAPIQueryFilter = (query, filter) => {
  const getFilter = () => {
    if (isJSONString(filter)) {
      return JSON.parse(filter);
    }

    return {};
  };

  return {
    ...omitBy(query, isNull),
    ...getFilter(),
  };
};

// quick way to identify monitoring accounts
const isMonitoringAccount = userInfo => {
  const { admin = false, email, publisherID } = userInfo;
  const allowedEmails = [`${publisherID}@mysoundwise.com`];
  if (!admin || !allowedEmails.includes(email[0])) {
    return false;
  }

  return true;
};

/**
 * Properly extract the duration of a file processed by ffmpeg
 * @param file - the ffmpeg processed file
 */
const extractFfmpegAudioDuration = file => {
  const rawTime = getPath(file, 'metadata.duration.raw', '');
  const duration = moment.duration(rawTime).asSeconds();
  return Number(duration);
};

// extract publisher iaphub details
const extractPublisherIAPHubDetails = (publisher = {}) => {
  let {
    appName, appstoreUrl, playstoreUrl,
    onlyMobileAccessToTracks = false,
    notIaphubExists = true,
  } = whiteLabelAppConfig;
  if (!isEmpty(publisher) && canPublisherAccessFeature(publisher, 'MOBILE_WHITE_LABEL')) {
    appName = getPath(publisher, 'iaphub.appName', appName);
    appstoreUrl = getPath(publisher, 'iaphub.appstoreUrl', appstoreUrl);
    playstoreUrl = getPath(publisher, 'iaphub.playstoreUrl', playstoreUrl);
    onlyMobileAccessToTracks = getPath(publisher, 'onlyMobileAccessToTracks', onlyMobileAccessToTracks);
    notIaphubExists = isEmpty(publisher.iaphub);
  }
  return {
    appName, appstoreUrl, playstoreUrl,
    onlyMobileAccessToTracks,
    notIaphubExists,
  };
};

const filterS3Urls = i =>
  i.url &&
  (i.url.includes('soundwiseinc') ||
    i.url.includes('mysoundwise.com') ||
    i.url.slice(0, 8) === '/tracks/');

const generateEpisodeId = () =>
  `${Number(Date.now() + Math.random())
    .toFixed(4)
    .replace('.', '')}e`;

const generateSoundcastId = () => Date.now() + 's';

const strToBoolean = str => !!str && str.toLowerCase() === 'true';

const errorToLogInfo = (err, prefix = '') => {
  if (isEmpty(err)) {
    return prefix;
  }
  const code = isEmpty(err.code) ? '' : ` code ${err.code}`;
  const message = isEmpty(err.message) ? '' : ` message "${err.message}"`;
  const info = (isEmpty(prefix) ? '' : prefix + ' ') +
    'error' + code + message + ' ' + JSON.stringify(err);
  return info.trim();
};

const encodeStripePlanID = ({publisherID, soundcastID, title, billingCycle, price}) => {
  let planID = `${publisherID}-${soundcastID}-${(title || '').replace(/-/g, '_')}-${billingCycle}-${price}`;
  return planID.replace(/ /g, '_').replace(/[^0-9a-z_\-]/gi, '');
};

const decodeStripePlanID = planID => {
  // planID === `${publisherID}-${soundcastID}-${title}-${billingCycle}-${price}`
  const vars = planID.split('-');
  const minLength = 5;
  if (vars.length < minLength) {
    return {};
  }

  const resultObj = {};
  const publisherID = vars.length > 0 ? vars[0] || '' : '';
  if (!isEmpty(publisherID) && publisherID.slice(0, 1) === '1' && publisherID.slice(-1) === 'p') {
    resultObj.publisherID = publisherID;
  }
  const soundcastID = vars.length > 1 ? vars[1] || '' : '';
  if (!isEmpty(soundcastID) && soundcastID.slice(0, 1) === '1' && soundcastID.slice(-1) === 's') {
      resultObj.soundcastID = soundcastID;
  }
  if (isEmpty(resultObj.publisherID) || isEmpty(resultObj.soundcastID)) {
    return {};
  }

  // const title = vars.length > 2 ? vars[2] || '' : '';
  // if there was a '-' symbol in the name, then the numbering was broken on encoding
  const billingCycle = getValidBillingCycle(
    vars.length >= minLength-1 ? vars[vars.length-2] || '' : ''
  );
  if (!isEmpty(billingCycle)) {
    resultObj.billingCycle = billingCycle;
  }

  return resultObj;
};

const getValidBillingCycle = billingCycleRaw => {
  const billingCycle = (billingCycleRaw || '').toLowerCase().replace('_',' ');
  return isOneTimeBillingCycle(billingCycle) || isRecurringBillingCycle(billingCycle)
    ? billingCycle
    : '';
};

const isOneTimeBillingCycle = billingCycle => {
  const testInterval = (billingCycle || '').toLowerCase();
  return ['one time', 'rental', 'one_time', 'onetime'].includes(testInterval);
};

const isRecurringBillingCycle = billingCycle => {
  const testInterval = (billingCycle || '').toLowerCase();
  const isRegularSubscription = ['monthly', 'quarterly', 'annual'].includes(testInterval);
  const isDevDaysSubscription = ['dev1daily', 'dev2daily'].includes(testInterval);
  return isRegularSubscription || isDevDaysSubscription;
};

const createStripeRecurringObj = billingCycle => {
  const testInterval = (billingCycle || '').toLowerCase();
  const recurring =
      isEqual(testInterval, 'monthly') ? { interval: 'month', interval_count: 1 }
    : isEqual(testInterval, 'quarterly') ? { interval: 'month', interval_count: 3 }
    : isEqual(testInterval, 'annual') ? { interval: 'year', interval_count: 1 }
    : isEqual(testInterval, 'dev1daily') ? { interval: 'day', interval_count: 1 }
    : isEqual(testInterval, 'dev2daily') ? { interval: 'day', interval_count: 2 }
    : {};
  if (isEmpty(recurring)) {
    throw new Error(`Unknown billingCycle ${JSON.stringify(billingCycle)}`);
  }
  return recurring;
};

const getBillingCycleFromStripeRecurringObj = recurring => {
  const { interval, interval_count = 0 } = recurring || {};
  const billingCycle =
    isEqual(interval, 'month') ? (
      interval_count == 1 ? 'monthly'
      : interval_count == 3 ? 'quarterly'
      : interval_count == 12 ? 'annual' // old style
      : null
    )
    : isEqual(interval, 'year') && isEqual(interval_count, 1) ? 'annual'
    : isEqual(interval, 'day') ? (
      interval_count == 1 ? 'dev1daily'
      : interval_count == 2 ? 'dev2daily'
      : null
    )
    : null;
  if (isEmpty(billingCycle)) {
    // throw new Error(`Unknown billingCycle for recurring ${JSON.stringify(recurring)}`);
    return `${interval || 'unknown' }_${interval_count || 0}`;
  }
  return billingCycle;
};

const shortDescriptionMaxPgLength = 4096; // Sequelize.STRING(4096),
const shortDescriptionPgCompatible = (description) => {
  return isEmpty(description) || description.length < shortDescriptionMaxPgLength
    ? description
    : description.slice(0, shortDescriptionMaxPgLength - 3) + '..';
};

module.exports = () => ({
  podcastCategories,
  currencies,
  getStripeAmount,
  getStripeCurrency,
  getStripeMinimumCharge,
  generateStripePlanId, // deprecated, replace with encodeStripePlanID
  getAuthSupportedPlan,
  getPlanFees,
  getPlanLimits,
  getPlanStatus,
  getPublisherPlan,
  getPublisherPlanInfo,
  isPublisherClass2,
  getSoundwiseProductInfo,
  getPlanFeatureAvailability,
  isCouponBelongsToPlan,
  isProduction,
  isStaging,
  isLocalDev,
  isFreePlan,
  getUpdatedPlanFromCoupon, // deprecated, use makePlanFromCoupon in future
  makePlanFromCoupon,
  getCouponInfo,
  isSoundcastDripped,
  accessExpirationWithGracePeriod,
  removeAccessExpirationGracePeriod,
  convertDecimalCommasToDots,
  getRandomColor,
  isFreeAccount,
  getSoundcastsIds,
  getEmailBody,
  getCurrentUnixTimestamp,
  getAudioWorkerRootHost,
  emailValidator,
  removeStripeInvalidCharacters,
  canPublisherAccessFeature,
  getPlanFeatures,
  getPlanFeatureLimits,
  isPublisherReferredByPartner,
  isPublisherPlan,
  isWithinDaysRange,
  isValidScheduledDate,
  slugify,
  lastnamePrivacy,
  replaceSpecialTags,
  trimSpaces,
  restoreDots,
  replaceDots,
  replaceDotsSafe,
  randomStringOfSize,
  httpToHttpsUrl,
  cloudfrontToS3Url,
  polishImageUrl,
  parseDraftJsTemplate,
  safeMathRound,
  isJSONString,
  attachAPIQueryFilter,
  isMonitoringAccount,
  extractFfmpegAudioDuration,
  extractPublisherIAPHubDetails,
  getPartners,
  getPartnerCodesValidity,
  getAllPartnerCodes,
  getPartnerCodes,
  filterS3Urls,
  generateEpisodeId,
  generateSoundcastId,
  strToBoolean,
  errorToLogInfo,
  encodeStripePlanID,
  decodeStripePlanID,
  getValidBillingCycle,
  isOneTimeBillingCycle,
  isRecurringBillingCycle,
  createStripeRecurringObj,
  getBillingCycleFromStripeRecurringObj,
  shortDescriptionPgCompatible,
});
